// Public interface of the aldoo.com product player (client-only island).
//
//   import PlayerHost, { openPlayer } from "components/Storefront/player"
//
// `PlayerHost` is mounted once in pages/Layout.jsx (outside the page tree) so
// the single player instance survives client-side navigations while
// minimized. `openPlayer(pid)` can be called from anywhere (it publishes a
// PubSub topic). The heavy player module is only imported on the first open.
import React, { useCallback, useEffect, useState } from "react"
import PubSub from "pubsub-js"

const PLAYER_TOPIC = "open-player"
/** Published whenever the player takes the screen, or gives it back. */
export const PLAYER_STATE_TOPIC = "player-state"
/** Asks the player to stop, whoever is about to make a sound of their own. */
export const PLAYER_PAUSE_TOPIC = "player-pause"

/**
 * Silence the player. Anything else on the page that starts playing — a
 * track preview, a trailer — says so first, so two things never sound at
 * once.
 */
export function pausePlayer() {
  PubSub.publish(PLAYER_PAUSE_TOPIC)
}

// The player covers the page while it is open, and nothing else belongs on
// top of it — the support widget included. A module-level flag answers for
// islands that mount later than the player opened (the widget waits for an
// idle browser), which a PubSub topic alone cannot.
let playerOpen = false
export const isPlayerOpen = () => playerOpen
const setPlayerOpen = (open) => {
  if (playerOpen === open) return
  playerOpen = open
  PubSub.publish(PLAYER_STATE_TOPIC, { open })
}

/**
 * Open the player for a product/album pid ("<slug>.<company>", or "book." / "product." for products created before).
 * @param {string} pid
 * @param {{autoPlay?: boolean, page?: number}} options page is 1-based
 */
export function openPlayer(pid, { autoPlay = true, page } = {}) {
  PubSub.publish(PLAYER_TOPIC, { pid, autoPlay, page })
}

let playerModule = null
const loadPlayerModule = () => {
  if (!playerModule) playerModule = import("./ProductPlayer.jsx")
  return playerModule
}

export default function PlayerHost() {
  const [Player, setPlayer] = useState(null)
  const [session, setSession] = useState(null) // {pid, autoPlay, page, key}
  const [minimized, setMinimized] = useState(false)
  const [request, setRequest] = useState(null) // page requested on an already open product

  useEffect(() => {
    const openToken = PubSub.subscribe(PLAYER_TOPIC, (_, data) => {
      if (!data?.pid) return
      loadPlayerModule().then((mod) => setPlayer(() => mod.default))
      setMinimized(false)
      setSession((current) => {
        if (current?.pid === data.pid) {
          setRequest({ page: data.page, autoPlay: data.autoPlay, id: Date.now() })
          return current
        }
        return { pid: data.pid, autoPlay: data.autoPlay, page: data.page, key: Date.now() }
      })
    })
    return () => PubSub.unsubscribe(openToken)
  }, [])

  // minimized, the player is a bar at the edge of the page: the page is
  // usable again, so what belongs to the page comes back
  useEffect(() => {
    setPlayerOpen(!!session && !minimized)
    return () => setPlayerOpen(false)
  }, [session, minimized])

  const close = useCallback(() => {
    setSession(null)
    setMinimized(false)
  }, [])
  const minimize = useCallback(() => setMinimized(true), [])
  const maximize = useCallback(() => setMinimized(false), [])

  if (!session || !Player) return null

  // No JSX: this file is .js (the Vite config only transpiles JSX in .jsx)
  return React.createElement(Player, {
    key: session.key,
    pid: session.pid,
    autoPlay: session.autoPlay,
    initialPage: session.page,
    request,
    minimized,
    onMinimize: minimize,
    onMaximize: maximize,
    onClose: close,
  })
}
